import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { SEARCH_CUSTOMERS_REQUEST_DATA } from '../../../store/customer/actions/search-customers-actions';

const CustomerAutoComplete = ({ value, onChange, placeholder, classNames, disabled = false }) => {
    const dispatch = useDispatch();
    const { data, isLoading, error } = useSelector((state) => state.searchCustomersData);
    const [inputValue, setInputValue] = useState(value || '');
    const [showDropdown, setShowDropdown] = useState(false);
    const defaultValueRef = useRef(value || '');

    useEffect(() => {
        setInputValue(value || '');
        defaultValueRef.current = value || '';
    }, [value]);

    useEffect(() => {
        const trimmedValue = (inputValue || '').toString().trim();
        if (!trimmedValue) {
            setShowDropdown(false);
            return;
        }
        if (inputValue === defaultValueRef.current) return;

        const handler = setTimeout(() => {
            if (trimmedValue.length >= 3) {
                dispatch({
                    type: SEARCH_CUSTOMERS_REQUEST_DATA,
                    payload: { params: { searchTerm: trimmedValue } }
                });
                setShowDropdown(true);
            }
        }, 500);
        return () => clearTimeout(handler);
    }, [inputValue, dispatch]);

    const handleSelect = useCallback((customer) => {
        const displayName = customer.name || `${customer.firstName} ${customer.lastName}`;
        setInputValue(displayName);
        setShowDropdown(false);
        if (onChange) onChange(customer);
    }, [onChange]);

    const handleBlur = () => {
        setTimeout(() => setShowDropdown(false), 200);
    };

    return (
        <div className={"relative " + classNames?.join(' ')}>
            <input
                type="text"
                value={inputValue}
                onChange={(e) => {
                    const newValue = (e.target.value || '').toString();
                    setInputValue(newValue);
                    if (!newValue.trim()) {
                        setShowDropdown(false);
                        if (onChange) onChange(null);
                    }
                }}
                onFocus={() => {
                    if (inputValue.toString().trim() && inputValue !== defaultValueRef.current) {
                        setShowDropdown(true);
                    }
                }}
                onBlur={handleBlur}
                placeholder={placeholder || "Search customer..."}
                className={`border p-2 rounded w-full ${disabled ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                disabled={disabled}
            />
            {isLoading && showDropdown && (
                <div className="absolute top-full left-0 bg-white p-2 text-sm text-gray-600">Loading...</div>
            )}
            {error && showDropdown && (
                <div className="absolute top-full left-0 bg-red-100 text-red-600 p-2 text-sm">{error}</div>
            )}
            {data?.value?.length > 0 && showDropdown && (
                <ul className="absolute z-10 bg-white border rounded w-full max-h-60 overflow-auto">
                    {data.value.map((customer) => (
                        <li
                            key={customer.id}
                            onMouseDown={() => handleSelect(customer)}
                            className="p-2 hover:bg-gray-200 cursor-pointer"
                        >
                            {customer.name || `${customer.firstName} ${customer.lastName}`} |
                            &nbsp;{customer.contactDetails.email}
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
};

export default CustomerAutoComplete;
